import { useEffect, useState } from "react";

import { Id } from "~/convex/_generated/dataModel";
import { api } from "~/convex/_generated/api";
import { createFileRoute } from "@tanstack/react-router";
import { useAction } from "convex/react";

export const Route = createFileRoute("/affiliates")({
  component: Affiliates,
});

export default function Affiliates() {
  const [status, setStatus] = useState<
    "processing" | "success" | "error" | "redirecting"
  >("processing");
  const [message, setMessage] = useState("");

  const exchangeOAuthCode = useAction(api.stripeApp.exchangeOAuthCode);

  useEffect(() => {
    console.log("🔍 AFFILIATES: useEffect triggered");

    // Read OAuth parameters from sessionStorage (where they were stored by main.tsx to avoid Convex Auth conflicts)
    const code = sessionStorage.getItem("stripe_oauth_code");
    const error = sessionStorage.getItem("stripe_oauth_error");
    const errorDescription = sessionStorage.getItem(
      "stripe_oauth_error_description",
    );
    const state = sessionStorage.getItem("stripe_oauth_state"); // This is the appId

    console.log("📋 AFFILIATES: OAuth parameters from sessionStorage:", {
      hasCode: !!code,
      codeLength: code?.length || 0,
      hasError: !!error,
      error,
      errorDescription,
      hasState: !!state,
      state,
    });

    // Clear sessionStorage after reading to prevent reprocessing
    if (code || error) {
      console.log("🧹 AFFILIATES: Clearing sessionStorage parameters");
      sessionStorage.removeItem("stripe_oauth_code");
      sessionStorage.removeItem("stripe_oauth_error");
      sessionStorage.removeItem("stripe_oauth_error_description");
      sessionStorage.removeItem("stripe_oauth_state");
    }

    // Check if this is a Stripe OAuth callback
    if (code || error) {
      console.log(
        "🎯 AFFILIATES: Processing Stripe OAuth callback on public route",
      );

      if (error) {
        console.log("❌ AFFILIATES: OAuth error from Stripe:", {
          error,
          errorDescription,
        });
        setStatus("error");
        setMessage(errorDescription || "Failed to connect Stripe account");
        // NO AUTO-REDIRECT - Let user see the error
        return;
      }

      if (code && state) {
        console.log("🔄 AFFILIATES: Starting OAuth code exchange", {
          codePreview: code.substring(0, 10) + "...",
          appId: state,
        });

        setMessage("Connecting your Stripe account...");

        exchangeOAuthCode({ code, appId: state as Id<"apps"> })
          .then((result) => {
            console.log("✅ AFFILIATES: OAuth exchange successful:", result);

            if (result.success) {
              setStatus("success");
              setMessage(
                `Stripe account connected successfully${result.livemode ? " (Live Mode)" : " (Test Mode)"}`,
              );

              // Redirect back to settings after success
              setTimeout(() => {
                console.log("🔄 AFFILIATES: Redirecting to settings page");
                setStatus("redirecting");
                setMessage("Redirecting back to settings...");
                window.location.href =
                  "/dashboard/settings/affiliates?success=stripe_connected";
              }, 2000);
            } else {
              console.log(
                "⚠️ AFFILIATES: Exchange returned success=false:",
                result,
              );
              setStatus("error");
              setMessage("Stripe connection failed - invalid response");
            }
          })
          .catch((error) => {
            console.error("💥 AFFILIATES: OAuth exchange failed with error:");
            console.error("Error object:", error);
            console.error("Error message:", error?.message);
            console.error("Error data:", error?.data);
            console.error("Full error:", JSON.stringify(error, null, 2));

            setStatus("error");
            setMessage(
              `Connection failed: ${error?.message || error?.data || "Unknown error"}`,
            );

            // NO AUTO-REDIRECT - Let user see the detailed error
          })
          .finally(() => {
            console.log("🏁 AFFILIATES: OAuth processing complete");
          });
      } else {
        console.log("❌ AFFILIATES: Missing required OAuth parameters", {
          hasCode: !!code,
          hasState: !!state,
        });
        setStatus("error");
        setMessage("Invalid OAuth callback - missing required parameters");
        // NO AUTO-REDIRECT - Let user see the error
      }
    } else {
      console.log("ℹ️ AFFILIATES: Not an OAuth callback, showing regular page");
      // Not an OAuth callback, just show the regular affiliates page
      setStatus("success");
      setMessage("");
    }
  }, [exchangeOAuthCode]);

  // If this is an OAuth callback, show processing status
  // Check if we have OAuth parameters in sessionStorage (indicates this is a callback)
  const hasOAuthCallback =
    sessionStorage.getItem("stripe_oauth_code") ||
    sessionStorage.getItem("stripe_oauth_error") ||
    // Also check if we just processed them (status would be set)
    status !== "success" ||
    message !== "";

  if (hasOAuthCallback) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="text-center">
            <div className="mb-4">
              {status === "processing" && (
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto"></div>
              )}
              {status === "success" && (
                <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center mx-auto">
                  <svg
                    className="h-5 w-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
              {status === "error" && (
                <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center mx-auto">
                  <svg
                    className="h-5 w-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              )}
              {status === "redirecting" && (
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent mx-auto"></div>
              )}
            </div>

            <h2 className="text-xl font-semibold mb-2">
              {status === "processing" && "Processing..."}
              {status === "success" && "Success!"}
              {status === "error" && "Error"}
              {status === "redirecting" && "Redirecting..."}
            </h2>

            {message && (
              <p
                className={`text-sm ${
                  status === "error"
                    ? "text-red-600"
                    : status === "success"
                      ? "text-green-600"
                      : "text-gray-600"
                }`}
              >
                {message}
              </p>
            )}

            {status === "error" && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-500">
                  Check the console for detailed error information.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() =>
                      (window.location.href = "/dashboard/settings/affiliates")
                    }
                    className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Back to Settings
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Regular affiliates page (when not processing OAuth)
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Affiliates</h1>
        <p className="text-gray-600 mb-4">Manage your affiliate program</p>
        <a
          href="/dashboard/settings/affiliates"
          className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Go to Affiliate Settings
        </a>
      </div>
    </div>
  );
}
